import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';
import { X } from 'react-feather';
import { ToolbarButton } from '../ConfigPages';
import { InlineMultiSelect } from '../InlineTable';
import { EtlBanner, EtlLoading, EtlSection, EtlGrid, EtlField } from '../EtlPages';
import EmailIntegrationConfig from '../SchemaBuilder/EmailIntegrationConfig';
import {
  updateFileTrackingConfig, getUsersInOrganization, getSchemaConfig, saveSchemaConfig,
} from '../../utils/api';

const DAYS = [
  { day: 0, label: 'Sun' }, { day: 1, label: 'Mon' }, { day: 2, label: 'Tue' },
  { day: 3, label: 'Wed' }, { day: 4, label: 'Thu' }, { day: 5, label: 'Fri' },
  { day: 6, label: 'Sat' },
];

/** Alert times are stored in UTC and edited in the operator's local time. */
const utcToLocalTime = (utcTime) => {
  if (!utcTime) return '';
  const [h, m] = utcTime.split(':').map(Number);
  const d = new Date();
  d.setUTCHours(h, m, 0, 0);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const localToUtcTime = (localTime) => {
  if (!localTime) return null;
  const [h, m] = localTime.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
};

/**
 * Feed options — everything you can change about one feed without opening the
 * schema wizard.
 *
 * This replaces TWO modals that each owned half the answer and were reached
 * from different places: FileTrackingConfigModal (alert times, expected days,
 * recipients) from the tracking views, and FileConfigEditModal (match
 * keywords, match mode, allowed extensions) from the DLQ. They edit the same
 * Cosmos fileConfig, so you could set an expected file name in one and a
 * conflicting match keyword in the other and never see the contradiction.
 * Two tabs, one dialog, one save path per tab.
 */
export default function FeedOptionsModal({ config, currentOrganization, onSave, onCancel }) {
  const hotelId = config?.hotelId;
  const fileType = config?.fileType;
  const isSubjectMatch = (config?.emailMatchType || 'file') === 'subject';

  const [tab, setTab] = useState('alerts'); // alerts | recognition
  const [error, setError] = useState(null);

  // ── Alerts & schedule ────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [form, setForm] = useState(() => ({
    // Falls back to auditFile: a feed that has never had tracking configured
    // still knows the file name it matches on.
    expectedFileName: config?.tracking?.expectedFileName || config?.auditFile || '',
    initialAlertTime: utcToLocalTime(config?.tracking?.initialAlertTime),
    secondaryAlertTime: utcToLocalTime(config?.tracking?.secondaryAlertTime),
    initialAlertUsers: [].concat(config?.tracking?.initialAlertUser || []),
    secondaryAlertUsers: [].concat(config?.tracking?.secondaryAlertUser || []),
    failedProcessUsers: [].concat(config?.tracking?.failedProcessUser || []),
    expectedDays: config?.tracking?.expectedDays || [0, 1, 2, 3, 4, 5, 6],
  }));

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingUsers(true);
        if (!currentOrganization?.code) return;
        const all = await getUsersInOrganization(currentOrganization.code);
        if (alive) setUsers(all || []);
      } catch (err) {
        console.error('Error loading users:', err);
        if (alive) setError('Failed to load the organization’s users.');
      } finally {
        if (alive) setLoadingUsers(false);
      }
    })();
    return () => { alive = false; };
  }, [currentOrganization?.code]);

  const userOptions = useMemo(() => (users || []).map((u) => ({
    value: u.email,
    label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
    sub: u.email,
  })), [users]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const toggleDay = (day) => {
    const current = form.expectedDays || [];
    set({
      expectedDays: current.includes(day)
        ? current.filter((d) => d !== day)
        : [...current, day].sort((a, b) => a - b),
    });
  };

  const saveAlerts = async (e) => {
    e.preventDefault();
    setError(null);
    // A recipient with no time would never be alerted — that is a silent
    // misconfiguration, so refuse it rather than saving a dead setting.
    if (form.initialAlertUsers.length && !form.initialAlertTime) {
      setError('Set an initial alert time, or remove its recipients.');
      return;
    }
    if (form.secondaryAlertUsers.length && !form.secondaryAlertTime) {
      setError('Set an escalation time, or remove its recipients.');
      return;
    }
    setSavingAlerts(true);
    try {
      await updateFileTrackingConfig(hotelId, fileType, {
        expectedFileName: form.expectedFileName || null,
        initialAlertTime: localToUtcTime(form.initialAlertTime),
        secondaryAlertTime: localToUtcTime(form.secondaryAlertTime),
        initialAlertUser: form.initialAlertUsers.length ? form.initialAlertUsers : null,
        secondaryAlertUser: form.secondaryAlertUsers.length ? form.secondaryAlertUsers : null,
        failedProcessUser: form.failedProcessUsers.length ? form.failedProcessUsers : null,
        expectedDays: form.expectedDays,
      });
      toast.success('Tracking options saved');
      onSave();
    } catch (err) {
      console.error('Error updating tracking config:', err);
      setError(err.message || 'Failed to save the tracking options');
    } finally {
      setSavingAlerts(false);
    }
  };

  // ── Recognition ──────────────────────────────────────────────
  const [schema, setSchema] = useState(null);
  const [emailConfig, setEmailConfig] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [schemaError, setSchemaError] = useState(null);
  const [savingSchema, setSavingSchema] = useState(false);

  const loadSchema = useCallback(async () => {
    if (!hotelId || !fileType) return;
    setLoadingSchema(true);
    setSchemaError(null);
    try {
      const res = await getSchemaConfig(hotelId, fileType);
      if (res?.success && res.data) {
        const cfg = res.data;
        setSchema(cfg);
        setEmailConfig({
          emailMatchType: 'file',
          matchMode: 'contains',
          ...(cfg.emailIntegration || {}),
          // A config written before emailIntegration existed keeps its match
          // string at the top level — read it rather than showing a blank box.
          auditFile: cfg.emailIntegration?.auditFile ?? cfg.auditFile ?? '',
          keywords: Array.isArray(cfg.emailIntegration?.keywords) ? cfg.emailIntegration.keywords : [],
          allowedExtensions: Array.isArray(cfg.emailIntegration?.allowedExtensions) ? cfg.emailIntegration.allowedExtensions : [],
          fileType: cfg.emailIntegration?.fileType ?? fileType,
        });
      } else {
        setSchemaError('No schema is saved for this feed yet — create one in the schema wizard.');
      }
    } catch (err) {
      setSchemaError(err.message || 'Failed to load the feed schema.');
    } finally {
      setLoadingSchema(false);
    }
  }, [hotelId, fileType]);

  useEffect(() => { if (tab === 'recognition' && !schema && !schemaError) loadSchema(); }, [tab, schema, schemaError, loadSchema]);

  const saveRecognition = async () => {
    if (!emailConfig) return;
    setSavingSchema(true);
    try {
      // Cosmos internals must not be echoed back on write.
      const { id: _id, _ts, _etag, _rid, _self, _attachments, ...rest } = schema || {};
      await saveSchemaConfig(hotelId, fileType, {
        ...rest,
        emailIntegration: { ...emailConfig, fileType: emailConfig.fileType || fileType },
        ocrService: schema?.ocrService,
        etlFunction: schema?.etlFunction,
      });
      toast.success('Recognition rules saved');
      onSave();
    } catch (err) {
      toast.error(`Failed to save: ${err.message || 'unknown error'}`);
    } finally {
      setSavingSchema(false);
    }
  };

  const body = (
    <div className="cp-modal-scrim" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="cp-modal cp-wide" role="dialog" aria-label="Feed options">
        <header className="cp-modal-head">
          <div>
            <h2>{config?.locationName || `Hotel ${hotelId}`}</h2>
            <div className="cp-code" style={{ marginTop: 2 }}>{config?.displayName || fileType}</div>
          </div>
          <div className="cp-tabs" role="tablist" aria-label="Feed options sections">
            <button type="button" role="tab" aria-selected={tab === 'alerts'}
                    className={tab === 'alerts' ? 'cp-active' : ''} onClick={() => setTab('alerts')}>
              Alerts &amp; schedule
            </button>
            <button type="button" role="tab" aria-selected={tab === 'recognition'}
                    className={tab === 'recognition' ? 'cp-active' : ''} onClick={() => setTab('recognition')}>
              Recognition
            </button>
          </div>
          <ToolbarButton iconOnly variant="ghost" icon={X} onClick={onCancel} aria-label="Close" />
        </header>

        {tab === 'alerts' ? (
          <form onSubmit={saveAlerts}>
            <div className="cp-modal-body">
              {error ? <div style={{ marginBottom: 14 }}><EtlBanner tone="error">{error}</EtlBanner></div> : null}

              <EtlSection title="What we expect">
                <EtlGrid single>
                  <EtlField
                    label={isSubjectMatch ? 'Expected subject line' : 'Expected file name'}
                    help={isSubjectMatch
                      ? 'The file is considered received when an email subject contains this text.'
                      : 'The file is considered received when an attachment name contains this text.'}
                  >
                    <input
                      type="text"
                      className="cp-input"
                      value={form.expectedFileName}
                      onChange={(e) => set({ expectedFileName: e.target.value })}
                      placeholder={isSubjectMatch ? 'Daily Audit Report — Hotel Name' : 'Daily_Audit_Report.xlsx'}
                    />
                  </EtlField>
                </EtlGrid>

                <div style={{ marginTop: 14 }}>
                  <div className="cp-label" style={{ marginBottom: 6 }}>Expected days</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {DAYS.map(({ day, label }) => (
                      <button
                        key={day}
                        type="button"
                        className={`cp-chip${(form.expectedDays || []).includes(day) ? ' cp-active' : ''}`}
                        aria-pressed={(form.expectedDays || []).includes(day)}
                        onClick={() => toggleDay(day)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="cp-help" style={{ marginTop: 6 }}>
                    Status and alerts only apply on the selected days — a feed that never runs at
                    weekends will not be reported as missing on a Saturday.
                  </div>
                </div>
              </EtlSection>

              <EtlSection
                title="Alerts"
                description="Times are local to you and stored in UTC."
              >
                <EtlGrid>
                  <EtlField label="Initial alert">
                    <input type="time" className="cp-input" value={form.initialAlertTime}
                           onChange={(e) => set({ initialAlertTime: e.target.value })} />
                  </EtlField>
                  <EtlField label="Escalation alert">
                    <input type="time" className="cp-input" value={form.secondaryAlertTime}
                           onChange={(e) => set({ secondaryAlertTime: e.target.value })} />
                  </EtlField>
                </EtlGrid>

                {loadingUsers ? <EtlLoading>Loading recipients…</EtlLoading> : (
                  <div style={{ marginTop: 14, display: 'grid', gap: 14 }}>
                    <EtlField label="Initial alert recipients">
                      <InlineMultiSelect
                        rowActive menuZIndex={999999}
                        value={form.initialAlertUsers}
                        options={userOptions}
                        onChange={(v) => set({ initialAlertUsers: v })}
                        placeholder="Nobody — no initial alert is sent"
                        ariaLabel="Initial alert recipients"
                      />
                    </EtlField>
                    <EtlField label="Escalation recipients">
                      <InlineMultiSelect
                        rowActive menuZIndex={999999}
                        value={form.secondaryAlertUsers}
                        options={userOptions}
                        onChange={(v) => set({ secondaryAlertUsers: v })}
                        placeholder="Nobody — no escalation is sent"
                        ariaLabel="Escalation recipients"
                      />
                    </EtlField>
                    <EtlField label="Processing failure recipients" help="Alerted when a file arrives but fails to process.">
                      <InlineMultiSelect
                        rowActive menuZIndex={999999}
                        value={form.failedProcessUsers}
                        options={userOptions}
                        onChange={(v) => set({ failedProcessUsers: v })}
                        placeholder="Nobody — failures are silent"
                        ariaLabel="Processing failure recipients"
                      />
                    </EtlField>
                    {!users.length ? (
                      <EtlBanner tone="warn">No users found in this organization, so no one can be alerted.</EtlBanner>
                    ) : null}
                  </div>
                )}
              </EtlSection>
            </div>

            <footer className="cp-modal-foot">
              <ToolbarButton variant="ghost" onClick={onCancel} type="button">Cancel</ToolbarButton>
              <button type="submit" className="cp-btn cp-primary" disabled={savingAlerts}>
                {savingAlerts ? 'Saving…' : 'Save alerts'}
              </button>
            </footer>
          </form>
        ) : (
          <>
            <div className="cp-modal-body">
              {loadingSchema ? <EtlLoading>Loading recognition rules…</EtlLoading> : null}
              {schemaError && !loadingSchema ? <EtlBanner tone="warn">{schemaError}</EtlBanner> : null}
              {!loadingSchema && !schemaError && emailConfig ? (
                <EmailIntegrationConfig initialConfig={emailConfig} onEmailConfigChange={setEmailConfig} />
              ) : null}
            </div>
            {!loadingSchema && !schemaError ? (
              <footer className="cp-modal-foot">
                <ToolbarButton variant="ghost" onClick={onCancel}>Cancel</ToolbarButton>
                <ToolbarButton variant="primary" onClick={saveRecognition} disabled={savingSchema}>
                  {savingSchema ? 'Saving…' : 'Save recognition'}
                </ToolbarButton>
              </footer>
            ) : null}
          </>
        )}
      </div>
    </div>
  );

  return createPortal(body, document.body);
}
